import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Form, Modal, Spinner } from "react-bootstrap";
import {
  useChangeClientOwnerEmailMutation,
  useGenerateClientPasswordSetupTicketMutation,
  useGetPasswordSetupEmailStatusQuery,
  useLazyGetOwnerAccountQuery,
  useSendClientPasswordSetupEmailMutation,
} from "@/features/accounts/accountsApi";
import type { ClientLifecycleView } from "@/features/accounts/clientLifecycle";
import {
  DetailField,
  DetailGrid,
  DetailSection,
} from "@/features/accounts/components/ClientDetailsFields";
import {
  SETUP_EMAIL_POLL_INTERVAL_MS,
  SETUP_EMAIL_POLL_WINDOW_MS,
  formatSetupEmailTimestamp,
  isSetupEmailTerminalStatus,
  setupEmailStatusLabel,
  shouldDismissSetupEmailSubmitNotice,
  shouldPollSetupEmailStatus,
  type SetupEmailOperatorStatus,
} from "@/features/accounts/setupEmailDeliveryStatus";
import SetupEmailStatusIndicator from "@/features/accounts/components/SetupEmailStatusIndicator";
import { formatUsPhoneInput } from "@/utils/phoneFormat";
import styles from "@/pages/accounts.module.scss";

export type OwnerEditFormSlice = {
  firstName: string;
  lastName: string;
  phone: string;
};

export type OwnerHydratedProfile = {
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  email: string | null;
};

type Props = {
  masterAccountId: string;
  view: ClientLifecycleView;
  /** When true, owner person fields become in-place inputs (not email). */
  editMode?: boolean;
  ownerForm?: OwnerEditFormSlice;
  onOwnerFormChange?: <K extends keyof OwnerEditFormSlice>(
    key: K,
    value: OwnerEditFormSlice[K],
  ) => void;
  /** Called when owner-account resolves (for edit hydration). */
  onOwnerHydrated?: (profile: OwnerHydratedProfile) => void;
  /** Bump after save to force a kk-profile re-read. */
  refreshToken?: number;
};

function formatOwnerName(
  displayName: string | null | undefined,
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): string {
  const fromParts = `${firstName || ""} ${lastName || ""}`.trim();
  if (fromParts) return fromParts;
  if (displayName?.trim()) return displayName.trim();
  return "—";
}

function formatLastSignIn(value: string | null | undefined): string {
  if (!value) return "Never";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Never";
  return parsed.toLocaleString();
}

function SetupStatusBadge({ status }: { status: string | null | undefined }) {
  if (status === "complete") {
    return (
      <Badge bg="success" pill data-testid="owner-account-setup-complete">
        Complete
      </Badge>
    );
  }
  if (status === "pending") {
    return (
      <Badge bg="warning" text="dark" pill>
        Pending
      </Badge>
    );
  }
  return (
    <Badge bg="secondary" pill>
      Unknown
    </Badge>
  );
}

const OwnerAccountPanel: React.FC<Props> = ({
  masterAccountId,
  view,
  editMode = false,
  ownerForm,
  onOwnerFormChange,
  onOwnerHydrated,
  refreshToken = 0,
}) => {
  const [fetchOwnerAccount, ownerQuery] = useLazyGetOwnerAccountQuery();
  const [generateTicket, { isLoading: generating }] =
    useGenerateClientPasswordSetupTicketMutation();
  const [sendEmail, { isLoading: emailing }] =
    useSendClientPasswordSetupEmailMutation();
  const [changeOwnerEmail, { isLoading: changingEmail }] =
    useChangeClientOwnerEmailMutation();

  const [passwordSetupUrl, setPasswordSetupUrl] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [emailFeedback, setEmailFeedback] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [trackedJobId, setTrackedJobId] = useState<string | null>(null);
  const [pollDeadlineMs, setPollDeadlineMs] = useState<number | null>(null);
  const [pollExhausted, setPollExhausted] = useState(false);
  const [showEmailEdit, setShowEmailEdit] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [emailEditError, setEmailEditError] = useState<string | null>(null);
  const [emailEditSuccess, setEmailEditSuccess] = useState<string | null>(null);

  useEffect(() => {
    setPasswordSetupUrl(null);
    setCopyFeedback(null);
    setEmailFeedback(null);
    setActionError(null);
    setTrackedJobId(null);
    setPollDeadlineMs(null);
    setPollExhausted(false);
    setShowEmailEdit(false);
    setNewEmail("");
    setConfirmEmail("");
    setEmailEditError(null);
    setEmailEditSuccess(null);
    if (masterAccountId) {
      void fetchOwnerAccount(masterAccountId);
    }
  }, [masterAccountId, fetchOwnerAccount, refreshToken]);

  useEffect(() => {
    const owner = ownerQuery.data;
    if (!owner || !onOwnerHydrated) return;
    onOwnerHydrated({
      firstName: owner.ownerFirstName,
      lastName: owner.ownerLastName,
      phone: owner.ownerPhone ?? null,
      email: owner.ownerEmail,
    });
  }, [ownerQuery.data, onOwnerHydrated]);

  const pollingInterval =
    pollDeadlineMs != null && !pollExhausted ? SETUP_EMAIL_POLL_INTERVAL_MS : 0;

  const statusQuery = useGetPasswordSetupEmailStatusQuery(
    { masterAccountId, jobId: trackedJobId || undefined },
    {
      skip: !masterAccountId,
      refetchOnMountOrArgChange: true,
      pollingInterval,
    },
  );
  const emailStatus = statusQuery.data;
  const operatorStatus = (emailStatus?.status ||
    "not_sent") as SetupEmailOperatorStatus;

  const pollActive = shouldPollSetupEmailStatus({
    status: operatorStatus,
    hasTrackedJob: Boolean(trackedJobId),
    pollCount: 0,
    maxPolls: Number.POSITIVE_INFINITY,
  });

  useEffect(() => {
    if (isSetupEmailTerminalStatus(operatorStatus)) {
      setPollDeadlineMs(null);
      return;
    }
    if (!pollActive || pollExhausted) {
      return;
    }
    setPollDeadlineMs(
      (prev) => prev ?? Date.now() + SETUP_EMAIL_POLL_WINDOW_MS,
    );
  }, [pollActive, operatorStatus, pollExhausted]);

  useEffect(() => {
    if (pollDeadlineMs == null || pollExhausted) return undefined;
    const remaining = pollDeadlineMs - Date.now();
    if (remaining <= 0) {
      setPollExhausted(true);
      setPollDeadlineMs(null);
      return undefined;
    }
    const handle = window.setTimeout(() => {
      setPollExhausted(true);
      setPollDeadlineMs(null);
    }, remaining);
    return () => window.clearTimeout(handle);
  }, [pollDeadlineMs, pollExhausted]);

  useEffect(() => {
    if (shouldDismissSetupEmailSubmitNotice(operatorStatus)) {
      setEmailFeedback(null);
    }
  }, [operatorStatus]);

  const owner = ownerQuery.data;
  const loading = ownerQuery.isLoading || ownerQuery.isFetching;
  const setupComplete = owner?.accountSetupStatus === "complete";

  const handleGenerate = async () => {
    setActionError(null);
    setCopyFeedback(null);
    setEmailFeedback(null);
    try {
      const result = await generateTicket({ masterAccountId }).unwrap();
      setPasswordSetupUrl(result.passwordSetupUrl);
    } catch (err: any) {
      setActionError(
        err?.data?.message ||
          err?.message ||
          "Failed to generate password setup link",
      );
    }
  };

  const handleCopy = async () => {
    if (!passwordSetupUrl) return;
    try {
      await navigator.clipboard.writeText(passwordSetupUrl);
      setCopyFeedback("Password reset link copied");
      setActionError(null);
    } catch {
      setActionError("Could not copy link — select and copy manually");
    }
  };

  const handleEmail = async () => {
    if (!passwordSetupUrl) return;
    setActionError(null);
    setEmailFeedback(null);
    try {
      const result = await sendEmail({
        masterAccountId,
        passwordSetupUrl,
      }).unwrap();
      setEmailFeedback(
        `Password reset email submitted for delivery to ${result.to}`,
      );
      setTrackedJobId(result.jobId || null);
      setPollExhausted(false);
      setPollDeadlineMs(Date.now() + SETUP_EMAIL_POLL_WINDOW_MS);
      void statusQuery.refetch();
    } catch (err: any) {
      setActionError(
        err?.data?.message ||
          err?.message ||
          "Failed to submit password reset email",
      );
    }
  };

  const openEmailEdit = () => {
    setEmailEditError(null);
    setEmailEditSuccess(null);
    setNewEmail("");
    setConfirmEmail("");
    setShowEmailEdit(true);
  };

  const submitEmailEdit = async () => {
    setEmailEditError(null);
    setEmailEditSuccess(null);
    const next = newEmail.trim().toLowerCase();
    const confirm = confirmEmail.trim().toLowerCase();
    if (!next || !next.includes("@")) {
      setEmailEditError("Enter a valid new email address");
      return;
    }
    if (next !== confirm) {
      setEmailEditError("New email and confirmation must match");
      return;
    }
    try {
      const result = await changeOwnerEmail({
        masterAccountId,
        newEmail: next,
        confirmEmail: confirm,
        confirmed: true,
      }).unwrap();
      setShowEmailEdit(false);
      setEmailEditSuccess(
        result.unchanged
          ? "Owner email unchanged"
          : `Owner email updated to ${result.ownerEmail}. Login and setup/reset emails now use this address.`,
      );
      setPasswordSetupUrl(null);
      void fetchOwnerAccount(masterAccountId);
    } catch (err: any) {
      const detailsCode = err?.data?.details?.code || err?.data?.code;
      if (detailsCode === "SHARED_MA_IDENTITY") {
        setEmailEditError(
          "This owner Auth0 identity is shared across multiple Clients. Changing the login email would affect every Client. Replace-owner is required instead.",
        );
        return;
      }
      setEmailEditError(
        err?.data?.message || err?.message || "Failed to change owner email",
      );
    }
  };

  const hasResetLink = Boolean(passwordSetupUrl);
  const generateButtonLabel = hasResetLink
    ? "Generate New Link"
    : "Generate Password Reset Link";
  const emailStatusLabel = setupComplete ? "Reset Email" : "Setup Email";

  return (
    <DetailSection
      title="Client Information"
      icon="bi bi-person"
      data-testid="client-owner-account"
    >
      {loading && !owner ? (
        <div className="text-muted small">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading owner account…
        </div>
      ) : (
        <>
          <DetailGrid>
            {/*
              Row 1: Name | Mobile Phone | Owner Email | Account Setup.
              "Name" is ONE stable grid cell in View + Edit. View composes
              kk-profile.firstName + lastName for display only; Edit swaps the
              composed text for two compact inputs bound separately to
              firstName / lastName (canonical fields stay separate — no fullName).
            */}
            <DetailField icon="bi bi-person" label="Name">
              {editMode ? (
                <div
                  className={styles.detailInlineInputRow}
                  data-testid="client-owner-name-edit"
                >
                  <div>
                    <Form.Control
                      size="sm"
                      className={styles.detailInlineInput}
                      placeholder="First"
                      aria-label="First Name"
                      value={ownerForm?.firstName ?? ""}
                      onChange={(e) =>
                        onOwnerFormChange?.("firstName", e.target.value)
                      }
                      autoComplete="off"
                      data-testid="client-owner-edit-first-name"
                    />
                    <span className={styles.detailInlineSubLabel}>
                      First Name
                    </span>
                  </div>
                  <div>
                    <Form.Control
                      size="sm"
                      className={styles.detailInlineInput}
                      placeholder="Last"
                      aria-label="Last Name"
                      value={ownerForm?.lastName ?? ""}
                      onChange={(e) =>
                        onOwnerFormChange?.("lastName", e.target.value)
                      }
                      autoComplete="off"
                      data-testid="client-owner-edit-last-name"
                    />
                    <span className={styles.detailInlineSubLabel}>
                      Last Name
                    </span>
                  </div>
                </div>
              ) : (
                <span data-testid="client-owner-name">
                  {formatOwnerName(
                    owner?.ownerDisplayName,
                    owner?.ownerFirstName,
                    owner?.ownerLastName,
                  )}
                </span>
              )}
            </DetailField>
            <DetailField icon="bi bi-telephone" label="Mobile Phone">
              {editMode ? (
                <Form.Control
                  size="sm"
                  className={styles.detailInlineInput}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="(888) 888-8888"
                  value={ownerForm?.phone ?? ""}
                  onChange={(e) =>
                    onOwnerFormChange?.(
                      "phone",
                      formatUsPhoneInput(e.target.value),
                    )
                  }
                  data-testid="client-owner-edit-phone"
                />
              ) : (
                <span data-testid="client-owner-phone">
                  {formatUsPhoneInput(owner?.ownerPhone || "") || "—"}
                </span>
              )}
            </DetailField>
            <DetailField
              icon="bi bi-envelope"
              label="Owner Email"
              labelAction={
                view === "active" && owner?.ownerEmail ? (
                  <button
                    type="button"
                    className={styles.detailFieldPencil}
                    title="Change owner email"
                    aria-label="Change owner email"
                    onClick={openEmailEdit}
                    data-testid="client-owner-change-email"
                  >
                    <i className="bi bi-pencil" aria-hidden="true" />
                  </button>
                ) : null
              }
            >
              <div data-testid="client-owner-email-display">
                {owner?.ownerEmail || "—"}
              </div>
            </DetailField>
            <DetailField icon="bi bi-shield-check" label="Account Setup">
              <SetupStatusBadge status={owner?.accountSetupStatus} />
            </DetailField>
            {/*
              Row 2: Last Sign-In | Setup/Reset Email | open | open.
              Remaining cells are intentionally left open for future
              Client Information fields.
            */}
            <DetailField icon="bi bi-clock-history" label="Last Sign-In">
              {formatLastSignIn(owner?.lastSignInAt)}
            </DetailField>
            <DetailField icon="bi bi-send" label={emailStatusLabel}>
              <div data-testid="client-owner-setup-email-status">
                <div className="d-inline-flex align-items-center">
                  <span>Status:&nbsp;</span>
                  <strong>{setupEmailStatusLabel(operatorStatus)}</strong>
                  <SetupEmailStatusIndicator status={operatorStatus} />
                </div>
                {operatorStatus === "sent" ? (
                  <div className={styles.detailFieldMeta}>
                    Mailgun accepted — not yet confirmed delivered to mailbox
                  </div>
                ) : null}
                {operatorStatus !== "not_sent" ? (
                  <div className={styles.detailFieldMeta}>
                    Last Attempt:{" "}
                    {formatSetupEmailTimestamp(emailStatus?.lastAttemptAt)}
                  </div>
                ) : null}
                {operatorStatus === "delivered" ? (
                  <div className={styles.detailFieldMeta}>
                    Delivered:{" "}
                    {formatSetupEmailTimestamp(emailStatus?.deliveredAt)}
                  </div>
                ) : null}
              </div>
            </DetailField>
          </DetailGrid>

          {emailEditSuccess ? (
            <Alert
              variant="light"
              className="py-2 px-3 mt-2 mb-0 small border"
              data-testid="client-owner-email-change-success"
            >
              {emailEditSuccess}
            </Alert>
          ) : null}

          {view === "active" && owner?.canGenerateSetupLink ? (
            <div className="mt-3">
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className={styles.detailsOutlineBtn}
                  onClick={() => void handleGenerate()}
                  disabled={generating}
                  data-testid="client-owner-generate-setup-link"
                >
                  <i className="bi bi-link-45deg" aria-hidden="true" />
                  {generating ? "Generating…" : generateButtonLabel}
                </Button>
                {setupComplete ? (
                  <div
                    className={`${styles.detailsInfoBanner} ${styles.detailsInfoBannerInline}`}
                    role="note"
                  >
                    <i className="bi bi-info-circle" aria-hidden="true" />
                    <span>
                      Account setup is complete. Use password reset only when the
                      owner needs a new login link — this is not initial
                      onboarding.
                    </span>
                  </div>
                ) : null}
                {hasResetLink ? (
                  <>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className={styles.detailsOutlineBtn}
                      onClick={() => void handleCopy()}
                      data-testid="client-owner-copy-link"
                    >
                      <i className="bi bi-clipboard" aria-hidden="true" />
                      Copy Link
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className={styles.detailsOutlineBtn}
                      onClick={() => void handleEmail()}
                      disabled={emailing}
                      data-testid="client-owner-email-link"
                    >
                      <i className="bi bi-envelope" aria-hidden="true" />
                      {emailing ? "Sending…" : "Send Reset Email"}
                    </Button>
                  </>
                ) : null}
                {copyFeedback ? (
                  <span className="text-muted small">{copyFeedback}</span>
                ) : null}
              </div>
            </div>
          ) : null}
          {view === "active" && owner && !owner.canGenerateSetupLink ? (
            <p className="text-muted small mt-3 mb-0">
              No canonical owner Auth0 identity is available for this Client.
            </p>
          ) : null}
          {emailFeedback ? (
            <Alert
              variant="light"
              className="py-2 px-3 mt-2 mb-0 small border"
              data-testid="client-owner-email-feedback"
            >
              {emailFeedback}
            </Alert>
          ) : null}
          {actionError ? (
            <Alert variant="danger" className="py-2 px-3 mt-2 mb-0 small">
              {actionError}
            </Alert>
          ) : null}
        </>
      )}

      <Modal
        show={showEmailEdit}
        onHide={() => setShowEmailEdit(false)}
        centered
        /* Nested over Client Details — same stack as Resolve Support Case. */
        className={styles.resolveSupportModal}
        backdropClassName={styles.resolveSupportBackdrop}
        enforceFocus
        restoreFocus
        data-testid="client-owner-change-email-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5 mb-0">Change owner email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted">
            Current: <strong>{owner?.ownerEmail || "—"}</strong>
          </p>
          <p className="small">
            This updates the Master Account Auth0 login email for this Client
            while preserving the same Auth0 identity. Password continuity is
            kept. Setup/reset emails will target the new address. Super Admin
            Auth0 is not changed.
          </p>
          <Form.Group className="mb-2">
            <Form.Label>New email</Form.Label>
            <Form.Control
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              autoComplete="off"
              data-testid="client-owner-new-email"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Confirm new email</Form.Label>
            <Form.Control
              type="email"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              autoComplete="off"
              data-testid="client-owner-confirm-email"
            />
          </Form.Group>
          {emailEditError ? (
            <Alert variant="danger" className="py-2 px-3 mt-3 mb-0 small">
              {emailEditError}
            </Alert>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowEmailEdit(false)}
            disabled={changingEmail}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void submitEmailEdit()}
            disabled={changingEmail}
            data-testid="client-owner-confirm-change-email"
          >
            {changingEmail ? "Saving…" : "Confirm change"}
          </Button>
        </Modal.Footer>
      </Modal>
    </DetailSection>
  );
};

export default OwnerAccountPanel;
